import React, { useState } from 'react';
import { Alert, Box, Button, InputAdornment, Stack, TextField } from '@mui/material';
import { Timer } from '@mui/icons-material';
import { checkHoldPlausibility, formatSeconds, parseDuration } from '../../utils/apneaTables';

/**
 * Dual-mode duration entry: timed by the app, or typed in by hand.
 *
 * Hand entry is not a fallback. A BOLT reading is taken before getting out of bed
 * and a max static is done lying down with eyes shut — in both cases the user may
 * be reading a bedside clock or being timed by someone else. An app that only
 * accepted its own timer would either go unused for these or push the user into a
 * worse position to operate it.
 *
 * Accepts `m:ss` or bare seconds. Implausible values are flagged for confirmation
 * rather than silently accepted or rejected, because a mistyped max static would
 * re-baseline the whole training plan.
 */

interface TimeEntryProps {
  label: string;
  /** Current value in seconds, or null when unset. */
  value: number | null;
  onChange: (seconds: number | null, source: 'manual' | 'corrected') => void;
  /** Prior values for the same measurement, used for the plausibility check. */
  history?: number[];
  /** True when this is editing a value the app timed, so provenance becomes 'corrected'. */
  wasTimed?: boolean;
  helperText?: string;
  onStartTimer?: () => void;
}

const TimeEntry: React.FC<TimeEntryProps> = ({
  label,
  value,
  onChange,
  history = [],
  wasTimed = false,
  helperText,
  onStartTimer,
}) => {
  const [text, setText] = useState(value === null ? '' : formatSeconds(value));
  const [warning, setWarning] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<number | null>(null);

  const commit = (seconds: number) => {
    onChange(seconds, wasTimed ? 'corrected' : 'manual');
    setWarning(null);
    setPendingConfirm(null);
  };

  const handleBlur = () => {
    if (text.trim() === '') {
      onChange(null, wasTimed ? 'corrected' : 'manual');
      setWarning(null);
      return;
    }

    const seconds = parseDuration(text);
    if (seconds === null) {
      setWarning('Enter a time as m:ss (for example 1:30) or a number of seconds.');
      setPendingConfirm(null);
      return;
    }

    const check = checkHoldPlausibility(seconds, history);
    if (!check.plausible) {
      // Flagged, not rejected — the user may genuinely have hit a new best.
      setWarning(check.reason ?? 'That value looks unusual.');
      setPendingConfirm(seconds);
      return;
    }

    commit(seconds);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label={label}
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder="1:30"
          helperText={helperText}
          fullWidth
          InputProps={{
            endAdornment: <InputAdornment position="end">m:ss</InputAdornment>,
          }}
        />
        {onStartTimer && (
          <Button
            variant="outlined"
            startIcon={<Timer />}
            onClick={onStartTimer}
            sx={{ mt: 1, whiteSpace: 'nowrap' }}
          >
            Time it
          </Button>
        )}
      </Stack>

      {warning && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          action={pendingConfirm !== null ? (
            <Button color="inherit" size="small" onClick={() => commit(pendingConfirm)}>
              Use it
            </Button>
          ) : undefined}
        >
          {warning}
        </Alert>
      )}
    </Box>
  );
};

export default TimeEntry;
